import { AnimatePresence, motion } from 'framer-motion';
import clsx from 'clsx';
import { Check } from 'lucide-react';
import { useCampaignWizard } from '../../hooks/use-campaign-wizard/use-campaign-wizard';
import { useCampaignWizardStore } from '../../stores/campaign-wizard-store';
import { WIZARD_STEPS, WIZARD_STEP_LABELS, type WizardStep } from '../../types/campaigns.types';
import { ObjectiveBudgetStep } from './steps/ObjectiveBudgetStep';
import { AudienceTargetingStep } from './steps/AudienceTargetingStep';
import { CreativeStep } from './steps/CreativeStep';
import { ReviewLaunchStep } from './steps/ReviewLaunchStep';
import { AdPreviewPanel } from './AdPreviewPanel/AdPreviewPanel';
import { AudienceEstimatePanel } from './AudienceEstimatePanel/AudienceEstimatePanel';
import { CredentialAlert } from './CredentialAlert';
import styles from './CampaignWizard.module.scss';

const stepVariants = {
  enter: (direction: number) => ({
    x: direction > 0 ? 50 : -50,
    opacity: 0,
  }),
  center: {
    x: 0,
    opacity: 1,
  },
  exit: (direction: number) => ({
    x: direction > 0 ? -50 : 50,
    opacity: 0,
  }),
};

const stepTransition = {
  duration: 0.25,
  ease: 'easeInOut' as const,
};

/**
 * Multi-step campaign creation wizard container.
 * Renders the current wizard step based on campaign-wizard-store state.
 */
export function CampaignWizard() {
  const {
    currentStep,
    currentStepIndex,
    completedSteps,
    totalSteps,
    nextStep,
    prevStep,
    goToStep,
    saveDraft,
    launch,
    isSaving,
    isLaunching,
  } = useCampaignWizard();

  const adAccountId = useCampaignWizardStore((state) => state.formData.adAccountId);

  const showAdPreview = currentStepIndex >= WIZARD_STEPS.indexOf('creative');
  const showAudienceEstimate = currentStep === 'audience-targeting';
  const showRightRail = showAdPreview || showAudienceEstimate;

  const handleGoToStep = (step: WizardStep) => {
    if (completedSteps.includes(step) || step === currentStep) {
      goToStep(step);
    }
  };

  const renderStep = () => {
    switch (currentStep) {
      case 'objective-budget':
        return (
          <ObjectiveBudgetStep
            onNext={nextStep}
            onBack={prevStep}
            onSaveDraft={saveDraft}
            isSaving={isSaving}
            showBack={currentStepIndex > 0}
          />
        );
      case 'audience-targeting':
        return (
          <AudienceTargetingStep
            onNext={nextStep}
            onBack={prevStep}
            onSaveDraft={saveDraft}
            isSaving={isSaving}
          />
        );
      case 'creative':
        return (
          <CreativeStep
            onNext={nextStep}
            onBack={prevStep}
            onSaveDraft={saveDraft}
            isSaving={isSaving}
          />
        );
      case 'review-launch':
        return (
          <ReviewLaunchStep
            onBack={prevStep}
            onSaveDraft={saveDraft}
            onLaunch={launch}
            onGoToStep={handleGoToStep}
            isSaving={isSaving}
            isLaunching={isLaunching}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className={styles.wizard}>
      {/* Progress indicator */}
      <nav className={styles.progress} aria-label="Wizard progress">
        <div className={styles.progressBar}>
          <div
            className={styles.progressFill}
            style={{ width: `${((currentStepIndex + 1) / totalSteps) * 100}%` }}
          />
        </div>
        <ol className={styles.stepList}>
          {WIZARD_STEPS.map((step, index) => {
            const isCompleted = completedSteps.includes(step);
            const isCurrent = step === currentStep;

            return (
              <li key={step} className={styles.stepItem}>
                <button
                  type="button"
                  className={clsx(
                    styles.stepDot,
                    isCurrent && styles.stepDotCurrent,
                    isCompleted && styles.stepDotCompleted,
                  )}
                  onClick={() => handleGoToStep(step)}
                  disabled={!isCompleted && !isCurrent}
                  aria-current={isCurrent ? 'step' : undefined}
                  aria-label={`${WIZARD_STEP_LABELS[step]} - Step ${index + 1} of ${totalSteps}`}
                >
                  {isCompleted ? <Check size={14} /> : index + 1}
                </button>
                <span className={clsx(styles.stepLabel, isCurrent && styles.stepLabelCurrent)}>
                  {WIZARD_STEP_LABELS[step]}
                </span>
              </li>
            );
          })}
        </ol>
      </nav>

      <div className={clsx(styles.body, !showRightRail && styles.bodyNoPreview)}>
        <div className={styles.main}>
          {/* Sits above the step content so a broken ad account is visible on
              every step, not only where it happens to break something. */}
          <CredentialAlert adAccountId={adAccountId} />
          {/* Step content with animation */}
          <div className={styles.content}>
            <AnimatePresence mode="wait" custom={1}>
              <motion.div
                key={currentStep}
                custom={1}
                variants={stepVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={stepTransition}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Live ad preview — shown from the creative step onward */}
        {showAdPreview && <AdPreviewPanel />}
        {/* Live reach estimate — shown during audience targeting */}
        {showAudienceEstimate && <AudienceEstimatePanel />}
      </div>
    </div>
  );
}
